'use client';

import { FC } from 'react';
import { ICmsTheme, IPromotedStore } from '../services/types';
import { ThemeLayout } from './ThemeLayout';
import Image from '@/src/components/Image';
import { Badge } from '@/src/components/ui/Badge';
import ExpressIcon from '@/src/components/icons/ExpressIcon';
import Typography from '@/src/components/ui/typography';
import { Link } from '@/src/i18n/navigation';
import { Routes } from '@/src/routes/routes';
import { ChevronRight } from 'lucide-react';
import Container from '@/src/components/ui/container';
import { SliderLayout } from '@/src/components/Layout/SliderLayout';
import { ProductCard } from '@/src/app/[locale]/stores/[store]/[storeId]/product/components/ProductCard';
import { PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { BasicProductDataResponse } from '@/src/types/catalog';

type Props = {
  data: IPromotedStore;
  theme?: ICmsTheme;
  globalLiveTimes?: Record<number, string>;
  name: string;
};

const StoreHeader: FC<{
  storeName: string;
  storeImage: string;
  storeDeliveryTime: string;
  storeUrl: string;
}> = ({ storeDeliveryTime, storeImage, storeName, storeUrl }) => (
  <div className="flex items-center justify-between">
    <div className="flex shrink-0 items-center justify-center gap-2">
      <Image
        src={storeImage}
        alt={storeName}
        width={150}
        height={150}
        size={PRODUCT_IMAGE_SIZE.MEDIUM}
        className="size-15 rounded-3xl object-cover shadow lg:size-20"
      />
      <div className="self-center text-start">
        <Typography component={'h2'} variant={'h5'} weight={'bold'} className="text-inherit">
          {storeName}
        </Typography>
        {storeDeliveryTime ? (
          <Badge className="space-x-1 px-1" variant={'primary'}>
            <ExpressIcon /> <span>{storeDeliveryTime}</span>
          </Badge>
        ) : null}
      </div>
    </div>
    <Link
      href={storeUrl}
      className="text-text flex items-center rounded-full bg-white px-2 py-2 text-base shadow"
    >
      <ChevronRight className="size-5 rtl:rotate-180" />
    </Link>
  </div>
);

export const PromotedStore: FC<Props> = ({ data, theme, globalLiveTimes, name }) => {
  const liveTime = globalLiveTimes?.[Number(data?.StoreId)] || '';

  const instedThem: ICmsTheme = {
    backgroundImage: data?.Theme?.Image ?? null,
    backgroundColor: data?.Theme?.Color ?? null,
  };

  const storeHeaderData = {
    storeName: name,
    storeImage: data?.StoreLogoUrl,
    storeDeliveryTime: liveTime,
    storeUrl: Routes.branchDetails(data?.StoreId, data?.EnglishStoreName),
  };

  return (
    <Container>
      <ThemeLayout
        id={data?.StoreId}
        theme={theme || instedThem || null}
        header={<StoreHeader {...storeHeaderData} />}
        alt={data?.StoreName}
      >
        <SliderLayout
          opts={{ slidesToScroll: 'auto', dragFree: true }}
          conatinerClassName="lg:ps-6 ps-3 mb-2"
          itemsClassName="basis-[46%] sm:basis-[30%] md:basis-[24.5%] lg:basis-[21%] shrink-0 md:pl-4 pl-2"
        >
          {data?.Products?.map((item: BasicProductDataResponse) => (
            <ProductCard key={item.integrationId} productDetails={item} scroll={true} />
          ))}
        </SliderLayout>
      </ThemeLayout>
    </Container>
  );
};
